import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useVoiceAssistant,
  useDataChannel,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { useNotificationHub, VoiceNoteNotification } from "../../hooks/use-notification-hub";
import VoiceNoteIndicator from "../voice-note-indicator/VoiceNoteIndicator";
import RecordingOverlay from "../recording-overlay/RecordingOverlay";
import MicButton from "../mic-button/MicButton";
import StatusText from "../status-text/StatusText";
import { TOKEN_ENDPOINT } from "../../config/constants";
import { getAudioStreamUrl } from "../../services/voice-notes-api";
import "./galena-session.scss";

const LOG = "[Galena]";

// Map LiveKit agent states to our UI states
type AgentState = "disconnected" | "connecting" | "initializing" | "listening" | "thinking" | "speaking";

function toConnectionState(agentState: AgentState): "disconnected" | "connecting" | "connected" {
  if (agentState === "disconnected") return "disconnected";
  if (agentState === "connecting" || agentState === "initializing") return "connecting";
  return "connected";
}

interface AgentInnerProps {
  onDisconnect: () => void;
  onVoiceNotePlay: (sender: string) => void;
  onVoiceNoteEnd: () => void;
  onRecordStart: (recipientName: string) => void;
}

function AgentInner({
  onDisconnect,
  onVoiceNotePlay,
  onVoiceNoteEnd,
  onRecordStart,
}: AgentInnerProps) {
  const { state } = useVoiceAssistant();
  const agentState = state as AgentState;
  const connectionState = toConnectionState(agentState);
  const isSpeaking = agentState === "speaking";

  // Log every agent state transition
  const prevStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevStateRef.current !== agentState) {
      console.log(
        `${LOG} Agent state: ${prevStateRef.current ?? "(init)"} -> ${agentState}  (connectionState=${connectionState})`
      );
      prevStateRef.current = agentState;
    }
  }, [agentState, connectionState]);

  // Listen for agent data messages on the "client_action" topic
  const { send } = useDataChannel("client_action", (msg) => {
    try {
      const text = new TextDecoder().decode(msg.payload);
      const data = JSON.parse(text);
      console.log(`${LOG} DataChannel received on "client_action":`, data);

      if (data.action === "play_voice_note") {
        const audioUrl = data.audioUrl ?? getAudioStreamUrl(data.noteId);
        console.log(`${LOG} Voice note playback starting — sender="${data.sender}", url=${audioUrl}`);
        onVoiceNotePlay(data.sender ?? "");
        const audio = new Audio(audioUrl);
        audio.crossOrigin = "anonymous";
        audio.onended = () => {
          console.log(`${LOG} Voice note playback ended`);
          onVoiceNoteEnd();
        };
        audio.onerror = (e) => {
          console.error(`${LOG} Voice note playback error:`, e);
          onVoiceNoteEnd();
        };
        audio.play().catch((err) => {
          console.error(`${LOG} Voice note play() rejected:`, err);
          onVoiceNoteEnd();
        });
      } else if (data.action === "record_reply") {
        console.log(`${LOG} Recording overlay requested — recipient="${data.recipientName}"`);
        onRecordStart(data.recipientName ?? "");
      }
    } catch (e) {
      console.error(`${LOG} DataChannel parse error:`, e);
    }
  });

  // Forward push notifications about new voice notes to the agent
  const handleNotification = useCallback(
    (msg: VoiceNoteNotification) => {
      console.log(`${LOG} Forwarding push notification to agent via DataChannel:`, msg);
      const payload = new TextEncoder().encode(
        JSON.stringify({
          type: "voice_note_received",
          noteId: msg.noteId,
          sender: msg.sender,
        })
      );
      send(payload, { reliable: true, topic: "client_event" }).catch((err) =>
        console.error(`${LOG} Failed to send notification to agent:`, err)
      );
    },
    [send]
  );
  useNotificationHub(handleNotification);

  const handleTap = useCallback(() => {
    if (connectionState === "connected") {
      console.log(`${LOG} User tapped mic while connected — triggering disconnect`);
      onDisconnect();
    }
  }, [connectionState, onDisconnect]);

  return (
    <>
      <div className="galena-session__center">
        <MicButton state={connectionState} volume={0} onTap={handleTap} />
        <StatusText state={connectionState} isSpeaking={isSpeaking} />
      </div>
      <RoomAudioRenderer />
    </>
  );
}

const GalenaSession: FC = () => {
  const [connectionDetails, setConnectionDetails] = useState<{
    token: string;
    url: string;
  } | null>(null);
  const [isPlayingVoiceNote, setIsPlayingVoiceNote] = useState(false);
  const [voiceNoteSender, setVoiceNoteSender] = useState("");
  const [recordingRecipient, setRecordingRecipient] = useState<string | null>(null);

  console.log(`${LOG} GalenaSession render — connected=${!!connectionDetails?.token}, voiceNote=${isPlayingVoiceNote}, recording=${recordingRecipient}`);

  const handleConnect = useCallback(async () => {
    console.log(`${LOG} Token fetch starting — endpoint=${TOKEN_ENDPOINT}`);
    // Set connecting state by setting a placeholder
    setConnectionDetails({ token: "", url: "" });
    try {
      const resp = await fetch(TOKEN_ENDPOINT);
      const data = await resp.json();
      console.log(`${LOG} Token fetch success — url=${data.url}, tokenLength=${data.token?.length ?? 0}`);
      setConnectionDetails(data);
    } catch (e) {
      console.error(`${LOG} Token fetch failed:`, e);
      setConnectionDetails(null);
    }
  }, []);

  const handleDisconnect = useCallback(() => {
    console.log(`${LOG} Disconnecting — clearing connection details`);
    setConnectionDetails(null);
  }, []);

  const handleVoiceNotePlay = useCallback((sender: string) => {
    console.log(`${LOG} Voice note indicator ON — sender="${sender}"`);
    setIsPlayingVoiceNote(true);
    setVoiceNoteSender(sender);
  }, []);

  const handleVoiceNoteEnd = useCallback(() => {
    console.log(`${LOG} Voice note indicator OFF`);
    setIsPlayingVoiceNote(false);
    setVoiceNoteSender("");
  }, []);

  const handleRecordStart = useCallback((recipientName: string) => {
    console.log(`${LOG} Recording overlay OPEN — recipient="${recipientName}"`);
    setRecordingRecipient(recipientName);
  }, []);

  const handleRecordComplete = useCallback(() => {
    console.log(`${LOG} Recording overlay COMPLETE`);
    setRecordingRecipient(null);
  }, []);

  const handleRecordCancel = useCallback(() => {
    console.log(`${LOG} Recording overlay CANCELLED`);
    setRecordingRecipient(null);
  }, []);

  if (!connectionDetails || !connectionDetails.token) {
    return (
      <div className="galena-session">
        <VoiceNoteIndicator isPlaying={false} sender="" />
        <div className="galena-session__center">
          <MicButton
            state={connectionDetails ? "connecting" : "disconnected"}
            volume={0}
            onTap={handleConnect}
          />
          <StatusText
            state={connectionDetails ? "connecting" : "disconnected"}
            isSpeaking={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="galena-session">
      <VoiceNoteIndicator isPlaying={isPlayingVoiceNote} sender={voiceNoteSender} />
      {recordingRecipient !== null && (
        <RecordingOverlay
          recipientName={recordingRecipient}
          onComplete={handleRecordComplete}
          onCancel={handleRecordCancel}
        />
      )}
      <LiveKitRoom
        serverUrl={connectionDetails.url}
        token={connectionDetails.token}
        connect={true}
        audio={true}
        onConnected={() => console.log(`${LOG} LiveKitRoom connected to ${connectionDetails.url}`)}
        onDisconnected={() => {
          console.log(`${LOG} LiveKitRoom disconnected`);
          handleDisconnect();
        }}
        onError={(err) => console.error(`${LOG} LiveKitRoom error:`, err)}
      >
        <AgentInner
          onDisconnect={handleDisconnect}
          onVoiceNotePlay={handleVoiceNotePlay}
          onVoiceNoteEnd={handleVoiceNoteEnd}
          onRecordStart={handleRecordStart}
        />
      </LiveKitRoom>
    </div>
  );
};

export default GalenaSession;
